<template>
  <Modal @cancel="close">
    <template v-slot:title>
      <p class="modal-card-title">Add a new FAQ</p>
    </template>

    <template v-slot:body>
      <div class="field">
        <label for="answer-modal-title-input" class="label">Frequently Asked Question</label>
        <div class="control">
          <input
            id="answer-modal-title-input"
            ref="faqNameInput"
            v-model="faqTitle"
            class="input"
            type="text"
            placeholder="Enter the Frequently Asked Question..."
            @input="$v.faqTitle.$touch()"
            @keypress.enter="addFAQ"
            :class="{ 'is-danger': $v.faqTitle.$invalid && $v.faqTitle.$dirty }"
          />
          <p class="help is-danger" v-if="$v.faqTitle.$invalid && $v.faqTitle.$dirty">{{ getErrorMessage() }}</p>
        </div>
      </div>
    </template>

    <template v-slot:buttons>
      <n-button-group>
        <n-button
          id="answer-modal-add-button"
          variant="primary"
          @click="addFAQ"
          :disabled="$v.$invalid"
          :loading="isLoading"
          >Add</n-button
        >
        <n-button id="answer-modal-close-button" @click="close" variant="primary--outline">Cancel</n-button>
      </n-button-group>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/shared/components/Modal.vue';
import appInsights from '@/shared/services/appInsights';
import Toast from '@/shared/services/toast';
import { AnswerViewModel } from '@/features/bots/models/answers/answerViewModel';
import { CREATE_ANSWER } from '@/store/answers/actionTypes';
import { CreateAnswerViewModel } from '@/features/bots/models/answers/createAnswerViewModel';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);

const createAnswer = (request: CreateAnswerViewModel): Promise<AnswerViewModel> =>
  store.dispatch(CREATE_ANSWER, request);

const faqTitle = ref('');
const isLoading = ref(false);
const faqNameInput = ref<HTMLInputElement | null>(null);

const emit = defineEmits<{
  (e: 'answer-added', answer: AnswerViewModel): void;
  (e: 'close'): void;
}>();

const validations = {
  faqTitle: {
    required,
    validFaqTitle: (value: string) => value.search(/[<>*%&:\\$]/) === -1,
    maxLength: maxLength(50),
  },
};

const $v = useVuelidate(validations, { faqTitle });

onMounted(() => {
  if (faqNameInput.value) {
    faqNameInput.value.focus();
  }
});

async function addFAQ() {
  if ($v.value.$invalid) {
    return;
  }

  appInsights.trackEvent({ name: 'Add answer clicked' }, { workspaceId: currentWorkspace.value.id });
  isLoading.value = true;
  try {
    faqTitle.value = faqTitle.value.trim();
    const createdAnswer = await createAnswer({ title: faqTitle.value });
    isLoading.value = false;
    emit('answer-added', createdAnswer);
    close();
  } catch (ex: any) {
    isLoading.value = false;
    if (ex.status === 409) {
      Toast.showError(`Failed to add FAQ - "${faqTitle.value}" already exists`);
    } else {
      Toast.showError('Failed to add FAQ');
    }
  }
}

function getErrorMessage(): string {
  if ($v.value.faqTitle.required.$invalid) {
    return 'Must not be empty';
  }

  if ($v.value.faqTitle.validFaqTitle.$invalid) {
    return 'Must not contain <, >, *, %, &, :, \\, $';
  }
  if ($v.value.faqTitle.maxLength.$invalid) {
    return 'Must not be longer than 50 characters';
  }

  return '';
}

function close() {
  emit('close');
}
</script>
